import * as Clipboard from "expo-clipboard";
import { Check, Copy } from "lucide-react-native";
import { FC, useState } from "react";

import {
  Button,
  ButtonIcon,
  ButtonText,
  HStack,
  Pressable,
  Spinner,
  Text,
  VStack,
} from "@/components/ui";
import API from "@/hooks/api";

import { formatTime } from "./colors";
import { FieldLabel } from "./Controls";
import type { County2024 } from "./model";
import Sheet from "./Sheet";
import type {
  SnapshotSamples,
  SnapshotSummariesByWeight,
  StateSample,
} from "./types";
import usePublish, { type PublishProgress } from "./usePublish";

/* ── Types ─────────────────────────────────────── */

interface PublishSheetProps {
  county2024: County2024 | null;
  countyError: string | null;
  /** `turnoutEdge` of the samples. */
  edge: number;
  /** True while the surveys load: publishing waits for them. */
  loading: boolean;
  onClose: () => void;
  open: boolean;
  samples: Record<string, StateSample>;
}

/* ── Constants ─────────────────────────────────── */

const CLIENT_PATH = "/midterms";

/* ── Helpers ───────────────────────────────────── */

const progressText = (progress: PublishProgress): string =>
  progress.step === "writing"
    ? `Writing summaries ${progress.done}/${progress.total}`
    : progress.step === "preparing"
      ? "Preparing the figures…"
      : "Saving the snapshot…";

/* ── Subcomponents ─────────────────────────────── */

/** Rendered while the sheet is open, so the snapshot is fetched then. */
const LastPublished: FC<{ publishedAt: string | null }> = ({ publishedAt }) => {
  const { error, loading, refresh, snapshot, unavailable } =
    API.Midterms.useGetSnapshot<SnapshotSamples, SnapshotSummariesByWeight>();

  if (publishedAt) {
    return (
      <Text className="text-sm text-typography-900">
        {formatTime(publishedAt)}
      </Text>
    );
  }
  if (loading) {
    return <Text className="text-sm text-typography-500">Checking…</Text>;
  }
  if (snapshot) {
    return (
      <Text className="text-sm text-typography-900">
        {formatTime(snapshot.published_at)}
      </Text>
    );
  }
  if (unavailable) {
    return (
      <Text className="text-sm text-typography-500">
        {unavailable === "missing"
          ? "Not published yet"
          : "This account can't read the published snapshot."}
      </Text>
    );
  }
  return (
    <Pressable className="self-start" onPress={refresh}>
      <Text className="text-sm text-error-600">{error} · retry</Text>
    </Pressable>
  );
};

/* ── Component ─────────────────────────────────── */

/** Publishes the client view and shares its link. */
const PublishSheet: FC<PublishSheetProps> = ({
  county2024,
  countyError,
  edge,
  loading,
  onClose,
  open,
  samples,
}) => {
  const { error, progress, publish, result } = usePublish();
  const [copied, setCopied] = useState(false);

  const link =
    typeof window === "undefined"
      ? CLIENT_PATH
      : `${window.location.origin}${CLIENT_PATH}`;
  const states = Object.keys(samples).length;
  const blocker = loading
    ? "Waiting for the surveys to load."
    : !county2024
      ? countyError
        ? `The 2024 county results didn't load (${countyError}); reload the page to publish.`
        : "Loading the 2024 county results…"
      : states === 0
        ? "No survey to publish yet."
        : null;

  const copy = async () => {
    await Clipboard.setStringAsync(link);
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const start = () => {
    if (county2024) void publish({ county2024, edge, samples });
  };

  const status = progress
    ? progressText(progress)
    : (error ??
      blocker ??
      (result
        ? `Published ${formatTime(result.publishedAt)}${result.missing > 0 ? ` · ${result.missing} summaries couldn't be written` : ""}`
        : `${states} surveyed states`));

  const footer = (
    <HStack className="items-center justify-between gap-3">
      <HStack className="flex-1 items-center gap-2">
        {progress && (
          <Spinner color="rgb(var(--color-primary-500))" size="small" />
        )}
        <Text
          className={`flex-1 text-sm ${error && !progress ? "text-error-600" : "text-typography-700"}`}
        >
          {status}
        </Text>
      </HStack>
      <Button
        className="rounded-xl"
        isDisabled={progress !== null || blocker !== null}
        onPress={start}
        size="sm"
      >
        <ButtonText>{error ? "Retry" : "Publish now"}</ButtonText>
      </Button>
    </HStack>
  );

  return (
    <Sheet
      footer={footer}
      onClose={onClose}
      open={open}
      subtitle="Clients see the last published snapshot, not the live tracker"
      title="Publish"
    >
      <VStack className="gap-1.5">
        <FieldLabel label="Last published" />
        <LastPublished publishedAt={result?.publishedAt ?? null} />
      </VStack>

      <VStack className="gap-1.5">
        <FieldLabel label="Client link" />
        <HStack className="items-center gap-3">
          <Text
            className="flex-1 text-sm text-typography-900"
            numberOfLines={1}
          >
            {link}
          </Text>
          <Button onPress={() => void copy()} size="sm" variant="outline">
            <ButtonIcon as={copied ? Check : Copy} />
            <ButtonText>{copied ? "Copied" : "Copy"}</ButtonText>
          </Button>
        </HStack>
        <Text className="text-xs text-typography-400">
          Opens for Foresight admins and accounts given midterms access.
        </Text>
      </VStack>

      <VStack className="gap-1.5">
        <FieldLabel label="What gets published" />
        <Text className="text-sm leading-5 text-typography-700">
          Each surveyed state’s latest run (respondents only, no study or run
          ids) and AI key insights for every race and for each office’s national
          picture, written at each own-vote weight clients can pick. Foresight
          numbers only: published forecasts and pasted sources stay internal.
          Writing the summaries takes a few minutes; keep this page open until
          it’s done.
        </Text>
      </VStack>
    </Sheet>
  );
};

export default PublishSheet;
